<template>
  <v-dialog v-model="dialogComputed" max-width="800px" persistent>
    <v-card-title class="primary white--text py-2">
      {{ $t('ELE0122') }}
      <v-spacer />
      <Btn icon="mdi-close" @click="close" depressed iconProp color="white" />
    </v-card-title>
    <v-card v-if="dialogComputed" class="py-4 px-4">
      <v-row>
        <v-col>
          <CompanyInput :value="returnCompanyCd" :disabled="true" />
        </v-col>
        <v-col>
          <TextFieldRef :label="$t('ELE0049')" :value="lotNo" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <PartCodeInput :value="partCd" :disabled="true" />
        </v-col>
        <v-col>
          <TextFieldRef :label="$t('ELE0119')" :value="acceptanceQtyComputed" right />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <TextFieldRef :label="$t('ELE0120')" :value="totalReturnQtyComputed" right />
        </v-col>
        <v-col>
          <TextFieldRef :label="$t('ELE0121')" :value="returnRateComputed" right />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-title class="pt-1 pb-0">
              {{ $t('ELE0023') }}
              <v-spacer />
            </v-card-title>
            <v-data-table :headers="headers" dense :items="returnRecordList">
              <template v-slot:[`item.action`]="{ item }">
                <IconInquiry @click="onInquiryItem(recordLink + item.returnNo)" left />
                <IconEdit @click="onEditItem(item)" right />
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import Btn from '@/components/atoms/base/button/Btn'
import { ModalMode } from '@/enum'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef.vue'
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import CompanyInput from '@/components/atoms/select/CompanyInput.vue'
import PartCodeInput from '@/components/atoms/select/PartCodeInput.vue'
import IconEdit from '@/components/atoms/icon/IconEdit.vue'
import IconInquiry from '@/components/atoms/icon/IconInquiry.vue'

export default {
  name: 'ReturnRecordDetailDialog',

  mixins: [APIUtils, Message, Loading],

  components: { Btn, TextFieldRef, CompanyInput, PartCodeInput, IconEdit, IconInquiry },

  props: {
    dialog: {
      type: Boolean,
      required: true,
      default: true,
    },
    returnNo: {
      type: String,
      required: false,
      default: null,
    },
    returnCompanyCd: {
      type: String,
      required: true,
      default: '',
    },
    lotNo: {
      type: String,
      required: false,
      default: '',
    },
    partCd: {
      type: String,
      required: true,
      default: '',
    },
    acceptanceQty: {
      type: [String, Number],
      required: true,
      default: '-',
    },
    modalMode: {
      type: String,
      required: false,
      default: ModalMode.INQUIRY,
    },
  },

  data() {
    return {
      returnRecord: {},
      returnRecordList: [],
      headers: [
        { text: '', align: 'center', divider: true, sortable: false, value: 'action' },
        { text: this.$t('ELE0136'), divider: true, value: 'returnNo' },
        { text: this.$t('ELE0135'), divider: true, value: 'returnDate' },
        { text: this.$t('ELE0120'), divider: true, align: 'end', value: 'returnQty' },
        { text: this.$t('ELE0054'), divider: false, value: 'remarks' },
      ],
    }
  },

  computed: {
    dialogComputed: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    recordLink: {
      get() {
        return `/edit-return-record?record=`
      },
    },
    acceptanceQtyComputed: {
      get() {
        return this.acceptanceQty?.toString() ?? '-'
      },
    },
    returnRateComputed: {
      get() {
        if (
          Number(this.acceptanceQty) > 0 &&
          Number(this.totalReturnQtyComputed) > 0 &&
          this.returnRecordList?.length > 0
        ) {
          return `${parseFloat((Number(this.totalReturnQtyComputed ?? 0) / Number(this.acceptanceQty)) * 100).toFixed(
            2,
          )}%`
        }
        return '-'
      },
    },
    totalReturnQtyComputed: {
      get() {
        const totalValue = 0
        return (
          this.returnRecordList?.reduce((prev, current) => {
            return prev + current.returnQty
          }, totalValue) ?? '-'
        )
      },
    },
  },
  methods: {
    close() {
      this.dialogComputed = false
    },
    async onInit() {
      //Display return result using Supplier, Lotno and Part code as params
      this.startLoading()
      try {
        const body = {
          returnCompanyCd: this.returnCompanyCd,
          lotNo: this.lotNo,
          partCd: this.partCd,
        }
        const response = await API.get('/record/returnRecord', body)
        if (response?.length > 0) {
          this.returnRecordList = response
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0122') })))
      } finally {
        this.stopLoading()
      }
    },
    onEditItem(item) {
      this.$router.push({ path: '/edit-return-record', query: { record: item.returnNo, edit: true } })
    },
    onInquiryItem(item) {
      this.$router.push({ path: item })
    },
  },
  mounted() {
    this.onInit()
  },
}
</script>
